import { styled } from '@mui/material/styles';
import { FileManagerComponent } from '@syncfusion/ej2-react-filemanager';

const StyledFileManager = styled(FileManagerComponent)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,

    // ── Toolbar ──
    '.e-toolbar': {
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      border: 'none',

      '.e-toolbar-items': {
        backgroundColor: theme.palette.background.paper,
      },
      '.e-toolbar-item': {
        backgroundColor: 'transparent',

        '.e-tbar-btn': {
          backgroundColor: 'transparent',
          color: theme.palette.text.primary,
          '&:hover, &:focus': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
          },
          '.e-icons': {
            color: theme.palette.text.secondary,
          },
          '.e-tbar-btn-text': {
            color: theme.palette.text.primary,
          },
        },
      },
      '.e-separator': {
        borderColor: theme.palette.divider,
      },
    },

    // ── Breadcrumb / Address bar ──
    '.e-address': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,

      '.e-addressbar-ul': {
        backgroundColor: theme.palette.background.default,

        '.e-list-text': {
          color: theme.palette.text.primary,
        },
        '.e-icons': {
          color: theme.palette.text.secondary,
        },
      },
      '.e-search-wrap': {
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,

        '.e-input': {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
        '.e-icons': {
          color: theme.palette.text.secondary,
        },
      },
    },

    // ── Navigation pane (tree view) ──
    '.e-navigation-pane': {
      backgroundColor: theme.palette.background.paper,
      borderRight: `1px solid ${theme.palette.divider}`,
    },

    '.e-treeview': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,

      '.e-list-item': {
        color: theme.palette.text.primary,
        backgroundColor: 'transparent',

        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '&.e-active, &.e-node-focus': {
          backgroundColor: theme.palette.action.selected,
          color: theme.palette.text.primary,

          '& > .e-fullrow': {
            backgroundColor: theme.palette.action.selected,
          },
        },
        '.e-text-content': {
          color: theme.palette.text.primary,
        },
        '.e-icons': {
          color: theme.palette.text.secondary,
        },
      },
      '.e-fullrow': {
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
      '.e-ul': {
        backgroundColor: 'transparent',
      },
    },

    // ── Splitter ──
    '.e-splitter': {
      borderColor: theme.palette.divider,
      backgroundColor: theme.palette.background.default,

      '.e-split-bar': {
        backgroundColor: theme.palette.divider,

        '&:hover': {
          backgroundColor: isDark ? '#30363D' : '#D0D7DE',
        },
      },
    },

    // ── Grid (details view) ──
    '.e-grid': {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      boxShadow: 'none',
      borderRadius: theme.shape.borderRadius,

      '& .e-gridcontent': {
        backgroundColor: theme.palette.background.default,
        '.e-content': {
          backgroundColor: theme.palette.background.default,
        },

        '.e-table': {
          borderCollapse: 'collapse',
          backgroundColor: theme.palette.background.default,

          '.e-row': {
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,

            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            '&.e-altrow': {
              backgroundColor: isDark ? '#0D1117' : '#FAFBFC',
            },
            '&.e-active, &.e-row-focus': {
              backgroundColor: theme.palette.action.selected,
              '& .e-rowcell': {
                color: theme.palette.text.primary,
              },
            },

            '.e-rowcell': {
              color: theme.palette.text.primary,
              backgroundColor: 'transparent',
              borderColor: theme.palette.divider,
              padding: '8px',

              '&.e-templatecell': {
                '.e-fe-text': {
                  color: theme.palette.text.primary,
                },
                '.e-fe-size': {
                  color: theme.palette.text.secondary,
                },
              },
              '.e-icons': {
                color: theme.palette.text.secondary,
              },
            },
          },
        },
      },
    },

    // ── Grid header ──
    '.e-headercontent': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.secondary,

      '.e-headercell': {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.secondary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderColor: theme.palette.divider,
      },
      '.e-icons': {
        color: theme.palette.text.secondary,
      },
      '.e-sortfilterdiv': {
        color: theme.palette.text.secondary,
      },
    },

    // ── Large icons view ──
    '.e-large-icons': {
      backgroundColor: theme.palette.background.default,

      '.e-list-parent': {
        backgroundColor: 'transparent',
      },
      '.e-list-item': {
        color: theme.palette.text.primary,

        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '&.e-active': {
          backgroundColor: theme.palette.action.selected,
        },
        '.e-list-text': {
          color: theme.palette.text.primary,
        },
        '.e-list-icon, .e-icons': {
          color: theme.palette.text.secondary,
        },
      },
    },

    // ── Buttons (scoped, not blanket primary) ──
    '.e-btn': {
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      borderColor: theme.palette.divider,

      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '&.e-primary': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderColor: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
      },
      '.e-icons': {
        color: 'inherit',
      },
    },

    // ── Context menu ──
    '.e-contextmenu-wrapper .e-menu-parent, .e-contextmenu-container .e-menu-parent': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.1)',

      '.e-menu-item': {
        color: theme.palette.text.primary,
        backgroundColor: 'transparent',

        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '&.e-focused': {
          backgroundColor: theme.palette.action.hover,
        },
        '&.e-disabled': {
          color: theme.palette.text.disabled,
        },
        '.e-menu-icon': {
          color: theme.palette.text.secondary,
        },
      },
      '.e-separator': {
        borderColor: theme.palette.divider,
      },
    },

    // ── Dialogs (rename, new folder, delete confirm, etc.) ──
    '& ~ .e-dialog, & ~ .e-popup': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderColor: theme.palette.divider,
    },

    // ── Upload dialog ──
    '.e-file-drop-area': {
      backgroundColor: theme.palette.background.default,
      borderColor: theme.palette.divider,
      color: theme.palette.text.secondary,
    },

    '.e-upload': {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,

      '.e-upload-files': {
        backgroundColor: theme.palette.background.default,
        borderColor: theme.palette.divider,
      },
      '.e-file-container': {
        color: theme.palette.text.primary,

        '.e-file-name': {
          color: theme.palette.text.primary,
        },
        '.e-file-type': {
          color: theme.palette.text.secondary,
        },
        '.e-file-size': {
          color: theme.palette.text.secondary,
        },
      },
    },

    // ── Status bar ──
    '.e-fe-status': {
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
    },

    // ── Global icon color fix ──
    '.e-icons:not(.e-btn-icon)': {
      color: theme.palette.text.secondary,
    },

    // ── Scrollbar for dark mode ──
    ...(isDark && {
      '& *::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '& *::-webkit-scrollbar-track': {
        backgroundColor: '#0D1117',
      },
      '& *::-webkit-scrollbar-thumb': {
        backgroundColor: '#30363D',
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: '#484F58',
        },
      },
    }),
  };
});

export default StyledFileManager;
